import { React, css, styled, useState, useEffect, useRef } from '../lib/imports';
import { ErrorModal, Heading, Input, Text, SubmitButton, Icon } from '../components';
import { batch, useDispatch, useSelector } from 'react-redux';
import { submitUsername, setUsernameValidationErr, resetErrorCodeStatus } from '../onboarding/actions';
import { isBoomUsernameValidationCode } from '../lib/graphql/adapters';

const ContainerBaseStyles = ({ theme }) => css`
  background: #2c2c2c;
  border-radius: ${theme.spacings.kilo};
  margin-bottom: ${theme.spacings.mega};
  span[role='alert'] {
    color: ${theme.colors.white};
  }
`;
const Container = styled('div')(ContainerBaseStyles);

const styledHeadingBaseStyles = ({ theme }) => css`
  padding: ${theme.spacings.giga};
  color: white;
`;
const StyledHeading = styled(Heading)(styledHeadingBaseStyles);

const nameEntryStyles = ({ theme }) => css`
  padding: 0px ${theme.spacings.giga} ${theme.spacings.kilo};
  margin-bottom: ${theme.spacings.mega};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const NameEntryContainer = styled('div')(nameEntryStyles);

const InputContainerStyles = ({ theme }) => css`
  flex-grow: 1;
  margin-right: ${theme.spacings.mega};
`;
const InputContainer = styled('div')(InputContainerStyles);

const ButtonContainerStyles = ({ theme }) => css`
  flex-shrink: 0;
  margin-top: ${theme.spacings.bit};
`;
const ButtonContainer = styled('div')(ButtonContainerStyles);

const successMessageContainerStyles = ({ theme }) => css`
  display: flex;
  flex-direction: row;
  background-color: #d1fae5;
  min-height: ${theme.spacings.zetta};
  padding: ${theme.spacings.kilo};
  justify-content: flex-start;
  align-items: center;
`;

const SuccessMessage = styled('div')(successMessageContainerStyles);

const iconWrapperBaseStyles = ({ theme }) => css`
  display: flex;
  margin: 0 ${theme.spacings.byte};
  height: ${theme.spacings.mega};
`;

const IconWrapper = styled('div')(iconWrapperBaseStyles);

const styledIconBaseStyles = ({ theme }) =>
  css`
    border-radius: ${theme.borderRadius.circle};
    padding: ${theme.spacings.atom};
    background-color: #3f2021;
    color: #d1fae5;
  `;

const StyledIcon = styled(Icon)(styledIconBaseStyles);

const UpdateUserName = () => {
  const { strings, onboarding, ui } = useSelector((state) => state);
  const dispatch = useDispatch();
  const { usernameValidationErr, isFetching, updateUsernameStatus, resetUserNameAttemptStatus, username } = onboarding;
  const [currentUsername, setUsername] = useState(username || '');
  const inputRef = useRef();
  const { hasFetchError } = ui;

  const executeScroll = () => {
    inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  useEffect(() => {
    if (username) setUsername(username);
  }, [username]);

  useEffect(() => {
    if (usernameValidationErr || isBoomUsernameValidationCode(onboarding.errorCode)) inputRef.current.focus();
  }, [usernameValidationErr, onboarding.errorCode]);

  const handleUsernameSubmit = () => {
    dispatch(submitUsername(currentUsername));
  };
  useEffect(() => {
    if (!hasFetchError && resetUserNameAttemptStatus) {
      dispatch(submitUsername(currentUsername));
    }
  }, [hasFetchError, resetUserNameAttemptStatus]);

  const heading = strings['update/username/heading'];
  const buttonText = strings['update/username/buttonText'];
  const boomValidationError = isBoomUsernameValidationCode(onboarding.errorCode);
  const showServerErrorModal = onboarding.errorCode === 'serverError';
  const errorText = usernameValidationErr
    ? strings['username/usernameLengthError/text']
    : boomValidationError
    ? strings['username/boomError/text'](onboarding.errorCode)
    : '';
  const successText = strings['update/username/success'];

  return (
    <Container>
      <StyledHeading as='h3' size='mega' noMarginBoth>
        {heading}
      </StyledHeading>
      <NameEntryContainer>
        <InputContainer>
          <Input
            minWidth={10}
            spellCheck={false}
            type='text'
            id='username'
            value={currentUsername}
            submitUsername
            onChange={(e) => {
              setUsername(e.target.value);
              batch(() => {
                dispatch(setUsernameValidationErr(false));
                dispatch(resetErrorCodeStatus());
              });
            }}
            onFocus={() => executeScroll()}
            validationHint={errorText}
            invalid={errorText.length > 0}
            maxLength='15'
            ref={inputRef}
            noMargin
          />
        </InputContainer>

        <ButtonContainer>
          <SubmitButton
            size='kilo'
            isLoading={isFetching}
            text={buttonText}
            variant='primary'
            onSelect={handleUsernameSubmit}
            stretch
          />
        </ButtonContainer>
      </NameEntryContainer>

      {updateUsernameStatus && (
        <SuccessMessage>
          <IconWrapper>
            <StyledIcon icon='check' />
          </IconWrapper>
          <Text noMargin>{successText}</Text>
        </SuccessMessage>
      )}
      {showServerErrorModal && (
        <ErrorModal
          title={strings['username/serverError/title']}
          message={strings['username/serverError/text']}
          buttonText={strings['notifications/errormodal/button']}
          onClose={() => dispatch(resetErrorCodeStatus())}
        />
      )}
    </Container>
  );
};

export default UpdateUserName;
